import { useMemo } from "react";
import cx from "classnames";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { useTabularDisplayContext } from "@/providers/TabularDisplayProvider";
import { Globals } from "@coreapp/CoreApp/Globals";
import { DISPLAY_MODES, enumUIModes } from "@coreapp/constants";
import { Alert, AlertTitle, Button, Flex } from "@theorgchartdev/storybook";
import { PlanReviewer, PlanSubmission, ReviewerStatus } from "@/Utils/Plan/PlanSubmisionsTypes";
import { appstr } from "@coreapp/utils/appstr";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { GetWSMgr } from "@/Utils/StateLib";

interface PlanApprovalStatusAlertContentProps {
  planSubmission: PlanSubmission;
  className?: string;
}

const PlanApprovalStatusAlertContent = ({
  planSubmission,
  className
}: PlanApprovalStatusAlertContentProps) => {
  const { handleSideBarModeChange } = useRightSidebarContext();
  const { changeDisplayMode, isTabularDisplay } = useTabularDisplayContext();
  const { dismissApprovalAlert, isApprovalAlertDismissed } = usePlanManagerContext();

  const wsMgr = GetWSMgr();
  const currentUser = wsMgr?.GetUserName();

  const pendingReviewers = useMemo(
    () =>
      planSubmission.reviewers.filter(
        (reviewer: PlanReviewer) => reviewer.status === ReviewerStatus.Pending
      ),
    [planSubmission.reviewers]
  );

  const reviewerNames = useMemo(() => {
    const names = pendingReviewers
      .map((reviewer: PlanReviewer) => {
        if (reviewer.username === currentUser) {
          return appstr("you");
        }
        return reviewer.displayName || reviewer.username;
      })
      .join(", ");

    return names;
  }, [pendingReviewers, currentUser]);

  const alertSeverity: "warning" | "info" = useMemo(() => {
    const currentUserIsPendingReviewer = pendingReviewers.some(
      (reviewer: PlanReviewer) => reviewer.username === currentUser
    );

    return currentUserIsPendingReviewer ? "warning" : "info";
  }, [pendingReviewers, currentUser]);

  const handleViewChanges = () => {
    dismissApprovalAlert();

    // If in tabular view, switch to chart view first
    if (isTabularDisplay) {
      changeDisplayMode(DISPLAY_MODES.CHART);
    }
    handleSideBarModeChange("planningActivity");
  };

  if (isApprovalAlertDismissed || pendingReviewers.length === 0) {
    return null;
  }

  return (
    <Alert
      severity={alertSeverity}
      className={cx("ocn-plan-approval-status-alert", className)}
      alertActions={{
        customAction: (
          <Flex gap="8px">
            <Button
              modifier="primary"
              color={alertSeverity}
              onClick={handleViewChanges}
              data-testid="plan-approval-review-changes-btn"
            >
              {appstr("Review changes")}
            </Button>
            <Button
              modifier="tertiary"
              color={alertSeverity}
              onClick={dismissApprovalAlert}
              iconProps={{
                name: "xmark"
              }}
              iconOnly
              title="Dismiss"
              aria-label="Dismiss"
              data-testid="planning-toolbar-plan-approval-dismiss-btn"
            />
          </Flex>
        )
      }}
    >
      <AlertTitle>{appstr("Plan requires approval")}</AlertTitle>
      {appstr("This plan is awaiting approval", { reviewerNames })}
    </Alert>
  );
};

const PlanApprovalStatusAlert = ({
  className
}: Pick<PlanApprovalStatusAlertContentProps, "className">) => {
  const currentUIMode = Globals._uiConfig?.GetUIMode();
  const isPlanningMode = currentUIMode === enumUIModes.planning;
  const { planManager } = usePlanManagerContext();
  const planSubmission = planManager?.state?.GetPlanSubmission();

  if (!isPlanningMode || !planSubmission) {
    return null;
  }

  return <PlanApprovalStatusAlertContent planSubmission={planSubmission} className={className} />;
};

export default PlanApprovalStatusAlert;
